import { text } from '#contract'
import type { GraphicsSettings } from '#contract'
import { useClient } from '../../app/client-context.ts'
import { useView } from '../../app/use-view.ts'
import { Banner } from '../../components/banner.tsx'
import { Button } from '../../components/button.tsx'
import { Segmented } from '../../components/segmented.tsx'
import { Select } from '../../components/select.tsx'
import { SettingRow } from '../../components/setting-row.tsx'
import { Slider } from '../../components/slider.tsx'
import { Toggle } from '../../components/toggle.tsx'
import { useT } from '../../i18n/index.ts'
import { Text } from '../../i18n/text.tsx'
import { formatDegrees, formatPercent } from './format.ts'
import { ResetSection } from './reset-section.tsx'
import styles from './settings-screen.module.css'

const PRESETS = ['low', 'medium', 'high', 'ultra', 'custom'] as const
const LEVELS = ['off', 'low', 'high'] as const

function frameRateLimit(value: string): GraphicsSettings['frameRateLimit'] {
  if (value === '30') return 30
  if (value === '60') return 60
  return 0
}

/** Graphics options. A renderer change waits for a reload; everything else is live. */
export function GraphicsTab() {
  const t = useT()
  const client = useClient()
  const graphics = useView((view) => view.settings.graphics)
  const restartRequired = useView((view) => view.restartRequired)
  const notice = useView((view) => view.notice)

  const patch = (next: Partial<GraphicsSettings>): void => {
    client?.updateSettings({ graphics: next })
  }
  const levels = LEVELS.map((value) => ({ value, label: t(text(`settings.level.${value}`)) }))

  return (
    <div class={styles.stack}>
      {notice && (
        <Banner>
          <Text value={notice} />
        </Banner>
      )}
      <SettingRow
        label={t(text('settings.graphics.preset'))}
        description={t(text('settings.graphics.preset.description'))}
      >
        <Segmented
          value={graphics.preset}
          onChange={(preset) => {
            patch({ preset })
          }}
          options={PRESETS.map((preset) => ({
            value: preset,
            label: t(text(`settings.preset.${preset}`)),
            disabled: preset === 'custom',
          }))}
        />
      </SettingRow>
      <SettingRow
        label={t(text('settings.graphics.renderer'))}
        description={t(text('settings.graphics.renderer.description'))}
      >
        <Select
          value={graphics.renderer}
          onChange={(renderer) => {
            patch({ renderer })
          }}
          options={[
            { value: 'webgl2', label: t(text('settings.graphics.renderer.webgl2')) },
            { value: 'webgpu', label: t(text('settings.graphics.renderer.webgpu')) },
          ]}
        />
      </SettingRow>
      {restartRequired && (
        <Banner
          action={
            <Button
              onClick={() => {
                location.reload()
              }}
            >
              {t(text('settings.graphics.restartNow'))}
            </Button>
          }
        >
          {t(text('settings.graphics.restart'))}
        </Banner>
      )}
      <SettingRow label={t(text('settings.graphics.resolution'))}>
        <Slider
          min={0.5}
          max={1}
          step={0.05}
          value={graphics.resolutionScale}
          format={formatPercent}
          onChange={(resolutionScale) => {
            patch({ resolutionScale })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.graphics.antialiasing'))}>
        <Segmented
          value={graphics.antiAliasing}
          onChange={(antiAliasing) => {
            patch({ antiAliasing })
          }}
          options={[
            { value: 'fxaa', label: t(text('settings.graphics.antialiasing.fxaa')) },
            { value: 'msaa', label: t(text('settings.graphics.antialiasing.msaa')) },
          ]}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.graphics.shadows'))}>
        <Segmented
          value={graphics.shadows}
          options={levels}
          onChange={(shadows) => {
            patch({ shadows })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.graphics.ambientOcclusion'))}>
        <Segmented
          value={graphics.ambientOcclusion}
          options={levels}
          onChange={(ambientOcclusion) => {
            patch({ ambientOcclusion })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.graphics.reflections'))}>
        <Segmented
          value={graphics.reflections}
          options={levels}
          onChange={(reflections) => {
            patch({ reflections })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.graphics.bloom'))}>
        <Toggle
          checked={graphics.bloom}
          onChange={(bloom) => {
            patch({ bloom })
          }}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.graphics.fov'))}>
        <Slider
          min={70}
          max={110}
          step={1}
          value={graphics.fov}
          format={formatDegrees}
          onChange={(fov) => {
            patch({ fov })
          }}
        />
      </SettingRow>
      <SettingRow
        label={t(text('settings.graphics.frameRate'))}
        description={t(text('settings.graphics.frameRate.description'))}
      >
        <Segmented
          value={String(graphics.frameRateLimit)}
          onChange={(value) => {
            patch({ frameRateLimit: frameRateLimit(value) })
          }}
          options={[
            { value: '0', label: t(text('settings.graphics.frameRate.unlimited')) },
            { value: '30', label: t(text('settings.graphics.frameRate.30')) },
            { value: '60', label: t(text('settings.graphics.frameRate.60')) },
          ]}
        />
      </SettingRow>
      <SettingRow label={t(text('settings.graphics.showFps'))}>
        <Toggle
          checked={graphics.showFps}
          onChange={(showFps) => {
            patch({ showFps })
          }}
        />
      </SettingRow>
      <ResetSection section="graphics" />
    </div>
  )
}
